import {Platform} from 'react-native';
import {setPushCaller, getPushCaller, requestUaStart, matchesPushCaller} from './voipPendingAnswer';
import {
    showIncomingCallNotification,
    scheduleIncomingCallFallback,
    cancelIncomingCallNotification,
    startIncomingCallActionRouting,
} from './incomingCallNotification';

/**
 * react-native-callkeep wrapper — the native incoming-call surface
 * (CallKit on iOS, self-managed ConnectionService on Android).
 *
 * This is what actually RINGS when the app is backgrounded or killed:
 *  - iOS: a PushKit VoIP push (see voipPushKit.js) wakes the app and MUST
 *    immediately report a call here (iOS 13+ requirement).
 *  - Android: a high-priority FCM data message wakes the module-scope
 *    background handler (index.js), which calls displayIncomingCallFromPush().
 *    CallKeep is SELF-MANAGED there, so Telecom shows no UI and plays no
 *    ringtone: the app posts its own call notification (ringtone + full-screen
 *    intent over the lock screen + Answer/Decline) via incomingCallNotification.js,
 *    on CallKeep's `showIncomingCallUi` (or a 500 ms fallback). The full-screen
 *    intent needs USE_FULL_SCREEN_INTENT — declared for the VoIP calling feature,
 *    requires the Play Console full-screen-intent declaration, and on Android 14+
 *    the user may have to grant it in settings.
 *
 * The native module is loaded lazily + guarded: until the dev-client/EAS build
 * that bundles it ships, every call is a safe no-op (never aborts boot — this
 * app is native-fragile, see the "stuck at splash" note in index.js).
 */

let RNCallKeep = null;
let callKeepReady = false;

/** callId (backend) → CallKit UUID map, so answer/end events resolve the call. */
const uuidByCallId = new Map();
const callIdByUuid = new Map();

/** Answer/end dedupe: a call can be answered/ended via CallKeep AND the notification. */
const answeredUuids = new Set();
/** uuid → end timestamp (remembered briefly so a late CallKeep `endCall` is ignored). */
const endedUuids = new Map();
const ENDED_MEMORY_MS = 60000;

/** Handlers passed to registerCallKeepEvents (reused by notification actions). */
let activeHandlers = null;

/** Localized label (i18n loaded lazily so a headless start never depends on it). */
function label(key, fallback) {
    try {
        // eslint-disable-next-line global-require
        const i18n = require('../../i18n').default;
        const value = i18n && typeof i18n.t === 'function' ? i18n.t(key, {defaultValue: fallback}) : fallback;

        return typeof value === 'string' && value ? value : fallback;
    } catch (_error) {
        return fallback;
    }
}

/** Payload for the Android incoming-call notification. */
function notificationPayload(callUUID, callerName, handle) {
    return {
        callUUID,
        callId: callIdByUuid.get(callUUID) || null,
        callerName: callerName || handle || label('voip_unknown_caller', 'Ismeretlen hívó'),
        handle: handle || null,
        incomingLabel: label('voip_incoming_call', 'Bejövő hívás'),
        answerLabel: label('voip_answer', 'Fogadás'),
        declineLabel: label('voip_reject', 'Elutasítás'),
        channelName: label('voip_notif_channel_name', 'Bejövő hívások'),
    };
}

/** Load react-native-callkeep once, tolerating its absence. */
function getCallKeep() {
    if (RNCallKeep !== null) {
        return RNCallKeep;
    }
    try {
        // eslint-disable-next-line global-require
        RNCallKeep = require('react-native-callkeep').default;
    } catch (_error) {
        RNCallKeep = false; // sentinel: attempted, unavailable
    }
    return RNCallKeep;
}

/** RFC-4122-ish v4 UUID (CallKit only needs a well-formed, unique id). */
function uuidv4() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/**
 * Lower-cased UUID, or '' when not a UUID. RNCallKeep reports `callUUID`
 * lower-cased in its events while Swift's `uuidString` is upper-case, so every
 * map key goes through here.
 * @param {*} value Raw uuid.
 * @returns {string}
 */
function normalizeUuid(value) {
    const uuid = typeof value === 'string' ? value.trim().toLowerCase() : '';

    return UUID_PATTERN.test(uuid) ? uuid : '';
}

/**
 * Stable UUID for a backend callId (reuse across push/answer/end events).
 * Prefers the backend's per-call `uuid` (the one the iOS PushKit handler has
 * already reported to CallKit natively), else mints one.
 * @param {string} [callId] Backend call id.
 * @param {string} [preferredUuid] Push payload `uuid`.
 * @returns {string}
 */
function uuidForCall(callId, preferredUuid) {
    const preferred = normalizeUuid(preferredUuid);
    const key = String(callId || preferred || uuidv4());
    if (!uuidByCallId.has(key)) {
        const uuid = preferred || uuidv4();
        uuidByCallId.set(key, uuid);
        callIdByUuid.set(uuid, key);
    }
    return uuidByCallId.get(key);
}

/**
 * Configure CallKeep. Safe to call more than once; only the first succeeds.
 * @returns {Promise<boolean>} Whether CallKeep is available and configured.
 */
export async function setupCallKeep() {
    const ck = getCallKeep();
    if (!ck || callKeepReady) {
        return callKeepReady;
    }
    try {
        await ck.setup({
            ios: {
                appName: 'Happy',
                supportsVideo: false,
                maximumCallGroups: '1',
                maximumCallsPerCallGroup: '1',
            },
            android: {
                alertTitle: 'Engedélyek szükségesek',
                alertDescription: 'A bejövő hívások fogadásához engedélyezd a telefonálást.',
                cancelButton: 'Mégse',
                okButton: 'Rendben',
                foregroundService: {
                    channelId: 'hu.happysolutions.app.voip',
                    channelName: 'Bejövő hívás',
                    notificationTitle: 'Aktív hívás',
                },
                additionalPermissions: [],
                selfManaged: true,
            },
        });
        if (Platform.OS === 'android') {
            ck.setAvailable(true);
            ck.registerPhoneAccount();
            ck.registerAndroidEvents();
            // Self-managed: Telecom asks US to show the incoming-call UI.
            ck.addEventListener('showIncomingCallUi', ({callUUID, name, handle} = {}) => {
                if (callUUID) {
                    showIncomingCallNotification(notificationPayload(callUUID, name, handle));
                }
            });
        }
        callKeepReady = true;
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[voip] CallKeep setup failed:', error?.message || error);
    }
    return callKeepReady;
}

/**
 * Ring the native incoming-call UI from an incoming-call push payload. Works
 * from a killed/backgrounded state (that is the whole point).
 *
 * @param {{uuid?: string, callId?: string, from?: string, fromNumber?: string, contactName?: string, suggestedName?: string}} rawData Push data.
 * @param {{alreadyReported?: boolean}} [options] `alreadyReported`: the iOS
 *   PushKit handler already reported this call to CallKit natively — only bind
 *   the uuid ↔ callId mapping and start SIP, never report a second call.
 * @returns {string|null} The CallKit UUID used, or null if CallKeep is absent.
 */
export function displayIncomingCallFromPush(rawData = {}, {alreadyReported = false} = {}) {
    // The backend now sends the caller as `fromNumber` (legacy payloads: `from`).
    const data = {...rawData, from: rawData.fromNumber || rawData.from};
    // Remember the caller (for INVITE matching) and start SIP registration right
    // away so the INVITE can arrive while the native UI is still ringing. On
    // Android headless there is no CallProvider yet — the request is deferred
    // until the activity launches on answer (see voipPendingAnswer.js).
    setPushCaller(data);
    requestUaStart();
    const ck = getCallKeep();
    if (!ck) {
        return null;
    }
    const uuid = uuidForCall(data.callId, data.uuid);
    if (alreadyReported) {
        return uuid;
    }
    const handle = data.from || 'ismeretlen';
    const name = data.contactName || data.from || 'Ismeretlen hívó';
    try {
        // best-effort setup (idempotent) so a cold push can ring before boot finishes
        setupCallKeep();
        ck.displayIncomingCall(uuid, handle, name, 'number', false);
        if (Platform.OS === 'android') {
            // Headless/killed start: `showIncomingCallUi` may never reach JS.
            scheduleIncomingCallFallback(notificationPayload(uuid, name, handle));
        }
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[voip] displayIncomingCall failed:', error?.message || error);
    }
    return uuid;
}

/**
 * The caller hung up / nobody answered (backend `call_ended` push): stop the
 * ringing native call and the Android call notification, and run the shared
 * end path once. Unknown calls are ignored.
 *
 * @param {{uuid?: string, callId?: string, endReason?: (number|string)}} rawData Push data.
 * @returns {boolean} Whether a known call was ended.
 */
export function endIncomingCallFromPush(rawData = {}) {
    const data = rawData || {};
    const uuid = normalizeUuid(data.uuid) || (data.callId ? uuidByCallId.get(String(data.callId)) : '') || '';
    if (!uuid || answeredUuids.has(uuid)) {
        return false;
    }
    const reason = Number(data.endReason) || 2; // CXCallEndedReason.remoteEnded
    cancelIncomingCallNotification(uuid);
    const ck = getCallKeep();
    try {
        if (ck) {
            ck.reportEndCallWithUUID(uuid, reason);
        }
    } catch (_error) {
        /* noop */
    }
    if (!callIdByUuid.has(uuid)) {
        return false;
    }
    fireEnd(uuid, data.callId ? String(data.callId) : undefined);

    return true;
}

/**
 * Wire the CallKeep action events to app logic.
 *
 * @param {{onAnswer: (info: {callId: string, uuid: string}) => void,
 *          onEnd: (info: {callId: string, uuid: string}) => void,
 *          onMute?: (muted: boolean, info: object) => void}} handlers
 * @returns {() => void} Unsubscribe.
 */
export function registerCallKeepEvents({onAnswer, onEnd, onMute}) {
    const ck = getCallKeep();
    if (!ck) {
        return () => {};
    }
    activeHandlers = {onAnswer, onEnd};

    const answer = ({callUUID} = {}) => fireAnswer(callKeepUuid(callUUID));
    const end = ({callUUID} = {}) => fireEnd(callKeepUuid(callUUID));
    const mute = ({muted, callUUID} = {}) => onMute && onMute(muted, resolveCall(callKeepUuid(callUUID)));
    // Cold launch (iOS): the user can answer/decline on the lock screen before
    // JS is up — RNCallKeep buffers those actions and replays them here.
    const replay = (events) => {
        (Array.isArray(events) ? events : []).forEach((event) => {
            if (event?.name === 'RNCallKeepPerformAnswerCallAction') {
                answer(event.data);
            } else if (event?.name === 'RNCallKeepPerformEndCallAction') {
                end(event.data);
            }
        });
    };

    ck.addEventListener('answerCall', answer);
    ck.addEventListener('endCall', end);
    ck.addEventListener('didPerformSetMutedCallAction', mute);
    ck.addEventListener('didLoadWithEvents', replay);

    return () => {
        ck.removeEventListener('answerCall');
        ck.removeEventListener('endCall');
        ck.removeEventListener('didPerformSetMutedCallAction');
        ck.removeEventListener('didLoadWithEvents');
        activeHandlers = null;
    };
}

/** CallKeep event uuid → map key (lower-cased; non-UUID ids pass through). */
function callKeepUuid(callUUID) {
    return normalizeUuid(callUUID) || callUUID;
}

/** Resolve a CallKeep UUID to `{uuid, callId}`. */
function resolveCall(uuid, fallbackCallId) {
    return {uuid, callId: callIdByUuid.get(uuid) || fallbackCallId || uuid};
}

/** Run the app's answer handler once per call (CallKeep event or notification). */
function fireAnswer(uuid, fallbackCallId) {
    if (!uuid || answeredUuids.has(uuid)) {
        return false;
    }
    answeredUuids.add(uuid);
    cancelIncomingCallNotification(uuid);
    if (activeHandlers && activeHandlers.onAnswer) {
        activeHandlers.onAnswer(resolveCall(uuid, fallbackCallId));
    }

    return true;
}

/** Run the app's end handler once per call (CallKeep event or notification). */
function fireEnd(uuid, fallbackCallId) {
    const now = Date.now();
    endedUuids.forEach((at, key) => {
        if (now - at > ENDED_MEMORY_MS) {
            endedUuids.delete(key);
        }
    });
    if (!uuid || endedUuids.has(uuid)) {
        return false;
    }
    endedUuids.set(uuid, now);
    cancelIncomingCallNotification(uuid);
    if (activeHandlers && activeHandlers.onEnd) {
        activeHandlers.onEnd(resolveCall(uuid, fallbackCallId));
    }
    forgetCall(uuid);

    return true;
}

/**
 * Route the Android call-notification actions into the CallKeep answer/end path:
 * Answer → the same handler as a native answer (+ moves the Telecom connection
 * to answered), Decline → the end handler (the receiver already rejected the
 * connection natively), full-screen "show" → `onShow` (open the ringing screen).
 *
 * @param {{onShow?: (info: {uuid: string, callId: string, from: (string|null), contactName: string}) => void}} [options]
 * @returns {() => void} Unsubscribe.
 */
export function startIncomingCallNotificationRouting({onShow} = {}) {
    return startIncomingCallActionRouting({
        onAnswer: (launch) => {
            const uuid = launch.callUUID;
            const fired = fireAnswer(uuid, launch.callId);
            const ck = getCallKeep();
            if (fired && ck && callIdByUuid.has(uuid)) {
                try {
                    ck.answerIncomingCall(uuid);
                } catch (_error) {
                    /* connection gone — the in-app answer path still runs */
                }
            }
        },
        onDecline: (launch) => fireEnd(launch.callUUID, launch.callId),
        onShow: (launch) => onShow && onShow({
            uuid: launch.callUUID,
            callId: callIdByUuid.get(launch.callUUID) || launch.callId || launch.callUUID,
            from: launch.handle || null,
            contactName: launch.callerName || '',
        }),
    });
}

/**
 * Answer the native call from the in-app ringing screen (stops the Android
 * notification ringtone and runs the shared answer path once).
 * @param {string} callId Backend call id.
 * @returns {void}
 */
export function answerNativeCall(callId) {
    if (Platform.OS !== 'android') {
        return;
    }
    const uuid = uuidByCallId.get(String(callId));
    if (!uuid || answeredUuids.has(uuid)) {
        return;
    }
    const ck = getCallKeep();
    try {
        if (ck) {
            ck.answerIncomingCall(uuid);
        }
    } catch (_error) {
        /* noop */
    }
    fireAnswer(uuid);
}

/** @returns {({callId: string, from: string, contactName: string}|null)} Caller from the last wake push. */
export function getLastPushCaller() {
    return getPushCaller();
}

/** Tell CallKit the call connected (start the call timer). */
export function reportCallConnected(callId) {
    const ck = getCallKeep();
    if (!ck) {
        return;
    }
    const uuid = uuidByCallId.get(String(callId));
    if (uuid) {
        ck.setCurrentCallActive(uuid);
    }
}

/** End the native call (from an in-app hangup or a remote end). */
export function endNativeCall(callId) {
    const ck = getCallKeep();
    if (!ck) {
        return;
    }
    const uuid = uuidByCallId.get(String(callId));
    if (uuid) {
        cancelIncomingCallNotification(uuid);
        ck.endCall(uuid);
        forgetCall(uuid);
    }
}

/**
 * The SIP session ended/failed remotely (caller hung up or cancelled while
 * ringing): end the native call — and with it the Android ringing notification —
 * by the explicit callId, else by matching the wake-push caller.
 * @param {string|null} callId Bound backend call id (may be null while ringing).
 * @param {string} [remoteNumber] Remote SIP user of the ended session.
 * @returns {void}
 */
export function endNativeCallForRemoteEnd(callId, remoteNumber) {
    if (callId && uuidByCallId.has(String(callId))) {
        endNativeCall(callId);

        return;
    }
    const caller = getPushCaller();
    if (caller && caller.callId && caller.from && remoteNumber && matchesPushCaller(remoteNumber, caller)) {
        endNativeCall(caller.callId);
    }
}

/** Bring the app to the foreground (Android; iOS handled by CallKit). */
export function bringAppToForeground() {
    const ck = getCallKeep();
    if (ck && Platform.OS === 'android') {
        try {
            ck.backToForeground();
        } catch (_error) {
            /* noop */
        }
    }
}

function forgetCall(uuid) {
    const callId = callIdByUuid.get(uuid);
    callIdByUuid.delete(uuid);
    answeredUuids.delete(uuid);
    if (callId) {
        uuidByCallId.delete(callId);
    }
}
